import { Directive, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { build, buildWithParams, pool } from '@fitogest/reactive-form-builder/utils';
import {
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { StorageManager } from '../../../../common/storage-manager.class';
import { BaseForm } from '../../../base-form';
import { DashboardService } from '../../../../service/events/dashboard.service';
import { FincasApiService } from '../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../service/api/clientes-api.service';
import { TrabajadoresApiService } from '../../../../service/api/trabajadores-api.service';
import { RequestHandler } from '../../../../service/OffService/request-handler';
import { MtoReactiveFormDefinition } from './mto-reactive-form-definition';

const DEFAULT_LOCATION_CASCADE_FIELDS = ['id_finca'] as const;
const CASCADE_FIELD_ORDER = ['id_finca', 'id_sector', 'ids_parcelas'] as const;
const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const DROPDOWN_REFRESH_FIELD = '__dropdown_refresh';

type MtoLocationCascadeField = typeof CASCADE_FIELD_ORDER[number];

export interface MtoReactiveFormRuntimeOptions {
    readonly loadingLabel: string;
    readonly errorContext: string;
    readonly locationCascadeFields?: readonly MtoLocationCascadeField[];
}

@Directive()
export abstract class MtoReactiveFormComponent<
    TModel extends object,
    TForm extends MtoReactiveFormDefinition<TModel>,
> extends BaseForm<TModel> implements OnInit {
    public form!: TForm;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public loadingData = true;
    public initialPreviousData: TModel | null = null;
    public reactiveFormRecordId = '';
    public getRequest!: RequestHandler<TModel[]>;
    public override model = {} as TModel;

    public readonly reactiveFormLoadingLabel: string;

    private readonly reactiveFormErrorContext: string;
    private readonly locationCascadeFields: readonly MtoLocationCascadeField[];
    private lastReloadedClientDropdownId = '';
    private isReloadingClientDropdowns = false;

    protected constructor(
        route: ActivatedRoute,
        router: Router,
        dashboard: DashboardService,
        protected readonly fincasApi: FincasApiService,
        protected readonly clientesApi: ClientesApiService,
        protected readonly trabajadoresApi: TrabajadoresApiService,
        putRequest: RequestHandler<TModel[]>,
        postRequest: RequestHandler<TModel[]>,
        getRequest: RequestHandler<TModel[]>,
        pathToGoBack: string,
        headerTitleWhenEdit: string,
        headerTitleWhenNew: string,
        headerTitleWhenDuplicate: string,
        options: MtoReactiveFormRuntimeOptions,
    ) {
        super(
            route,
            router,
            dashboard,
            putRequest,
            postRequest,
            pathToGoBack,
            headerTitleWhenEdit,
            headerTitleWhenNew,
            headerTitleWhenDuplicate,
        );

        this.getRequest = getRequest;
        this.reactiveFormLoadingLabel = options.loadingLabel;
        this.reactiveFormErrorContext = options.errorContext;
        this.locationCascadeFields = options.locationCascadeFields ?? DEFAULT_LOCATION_CASCADE_FIELDS;
    }

    public async ngOnInit(): Promise<void> {
        this.dashboard.clientChanges$.subscribe(() => this.unsubscribeLocationCatalogs());

        this.reactiveFormRecordId = this.resolveReactiveFormRecordId();
        this.initializeHostModel((this.getModel() ?? {}) as TModel);
        this.initialPreviousData = { ...this.model };
        this.form = this.createFormDefinition();
        this.softInit(this.getType());
        this.resetExternalDropdowns();

        try {
            await this.initFormData();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            console.error('No se ha podido preparar el formulario MTO reactivo.', error);
        }
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<TModel>): void {
        const changedFieldName = String(event.changedField ?? '');

        this.form.model = {
            ...this.form.model,
            ...event.value,
        };
        this.syncModelFromForm();

        if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD) {
            this.applyLoadedReactiveState();
            return;
        }

        if (changedFieldName === DROPDOWN_REFRESH_FIELD) {
            this.syncModelFromForm();
            return;
        }

        if (event.origin !== 'user') {
            return;
        }

        this.handleFormValueChange(event.value, event.changedField);
    }

    public reactiveFormChanges(
        formModel: ReactiveFormValue<TModel>,
        lastChangedFieldName: ReactiveFormChangedField<TModel>,
    ): void {
        this.stableValueChanges({
            value: formModel,
            changedField: lastChangedFieldName,
            changedFields: [String(lastChangedFieldName ?? '')],
            origin: 'user',
        });
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<TModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }

        this.form.model = {
            ...this.form.model,
            ...event.value,
        };
        this.syncModelFromForm();
        this.applyLoadedReactiveState();
    }

    public handleFormValueChange(
        formModel: ReactiveFormValue<TModel>,
        lastChangedFieldName: ReactiveFormChangedField<TModel>,
    ): void {
        const changedFieldName = String(lastChangedFieldName);

        this.form.model = {
            ...this.form.model,
            ...formModel,
        };
        this.syncModelFromForm();

        this.syncCalculatedHours(changedFieldName);

        if (changedFieldName === 'mantenimiento') {
            this.form.updateMantenimientoLabel();
            this.refreshReactiveDropdownMappings();
        }

        if (changedFieldName === 'id_cliente') {
            this.clearLocationCascade(this.locationCascadeFields);
            this.onClientChanged();
            return;
        }

        const dependentFields = this.getDependentLocationFields(changedFieldName);

        if (dependentFields.length === 0) {
            return;
        }

        this.clearLocationCascade(dependentFields);
        this.refreshReactiveDropdownMappings();
    }

    public onAfterSend(): void {
        const model = this.form.model as Record<string, unknown>;

        StorageManager.saveSessionDataDonJardin(
            String(model['id_finca'] ?? ''),
            String(model['hora_inicio'] ?? ''),
            String(model['hora_fin'] ?? ''),
        );
    }

    private syncModelFromForm(): void {
        const model = this.form.model as Record<string, unknown>;

        this.updateHostModel({ ...model } as TModel);
    }

    protected abstract createFormDefinition(): TForm;

    private initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        const buildsRequest = [
            build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true),
            build(this.clientesApi.clientesGET, this.form.lists.clientes, { or: ['razon_social', 'nombre'] }),
            build(
                this.trabajadoresApi.trabajadoresGET,
                this.form.lists.trabajadores,
                (it: { nombre?: string; apellidos?: string }): string => `${it.nombre ?? ''} ${it.apellidos ?? ''}`
                    .trim(),
                true,
                true,
            ),
            ...this.getLocationCatalogBuilds(),
        ];

        return pool(buildsRequest, () => this.refreshReactiveDropdownMappings())
            .then(() => {
                this.loadingData = false;
                this.refreshReactiveDropdownMappings();

                return true;
            })
            .catch((error: unknown) => {
                this.loadingData = false;
                this.refreshReactiveDropdownMappings();
                console.error('Error loading MTO reactive form data.', error);
                throw error;
            });
    }

    private getLocationCatalogBuilds(): ReturnType<typeof build>[] {
        const builds: ReturnType<typeof build>[] = [];

        if (this.usesLocationField('id_sector')) {
            builds.push(build(this.fincasApi.sectoresGET, this.form.lists.sectores, 'nombre', true));
        }

        if (this.usesLocationField('ids_parcelas')) {
            builds.push(build(this.fincasApi.parcelasGET, this.form.lists.parcelas, 'nombre', true));
        }

        return builds;
    }

    private syncCalculatedHours(changedFieldName: string): void {
        const updates = this.form.getHourUpdatesForChangedField(changedFieldName);
        const updateEntries = Object.entries(updates);

        if (updateEntries.length === 0) {
            return;
        }

        this.form.model = {
            ...this.form.model,
            ...updates,
        };
        this.syncModelFromForm();

        this.formBuilderRef?.updateForm(updates as Partial<TModel>, true);
    }

    private onClientChanged(): void {
        const selectedClientId = this.getReactiveValueId((this.form.model as Record<string, unknown>)['id_cliente']);

        if (!selectedClientId || this.isReloadingClientDropdowns || selectedClientId === this.lastReloadedClientDropdownId) {
            this.refreshReactiveDropdownMappings();
            return;
        }

        this.isReloadingClientDropdowns = true;
        this.lastReloadedClientDropdownId = selectedClientId;

        pool(this.getClientScopedDropdownBuilds(selectedClientId), () => this.refreshReactiveDropdownMappings())
            .then(() => {
                this.syncFilteredLocationCatalogs();
                this.isReloadingClientDropdowns = false;
                this.refreshReactiveDropdownMappings();

                return true;
            })
            .catch((error: unknown) => {
                this.isReloadingClientDropdowns = false;
                this.refreshReactiveDropdownMappings();
                console.error('Error reloading MTO client dropdowns.', error);
            });
    }

    private getClientScopedDropdownBuilds(selectedClientId: string): ReturnType<typeof buildWithParams>[] {
        const builds = [
            buildWithParams(this.fincasApi.fincasGET, { id_cliente: selectedClientId }, this.form.lists.fincas, 'nombre', true),
        ];

        if (this.usesLocationField('id_sector')) {
            builds.push(buildWithParams(
                this.fincasApi.sectoresGET,
                { id_cliente: selectedClientId },
                this.form.lists.sectores,
                'nombre',
                true,
            ));
        }

        if (this.usesLocationField('ids_parcelas')) {
            builds.push(buildWithParams(
                this.fincasApi.parcelasGET,
                { id_cliente: selectedClientId },
                this.form.lists.parcelas,
                'nombre',
                true,
            ));
        }

        return builds;
    }

    private clearLocationCascade(fieldNames: readonly string[]): void {
        const updates: Record<string, null | unknown[]> = {};

        fieldNames.forEach((fieldName) => {
            updates[fieldName] = this.getEmptyLocationValue(fieldName);
        });

        this.form.model = {
            ...this.form.model,
            ...updates,
        } as Partial<TModel>;
        this.syncModelFromForm();
        this.formBuilderRef?.updateForm(updates as Partial<TModel>, false);
    }

    private getDependentLocationFields(fieldName: string): MtoLocationCascadeField[] {
        const sourceIndex = CASCADE_FIELD_ORDER.indexOf(fieldName as MtoLocationCascadeField);

        if (sourceIndex < 0) {
            return [];
        }

        return this.locationCascadeFields.filter((candidate) => CASCADE_FIELD_ORDER.indexOf(candidate) > sourceIndex);
    }

    private getEmptyLocationValue(fieldName: string): null | unknown[] {
        if (fieldName === 'id_sector' || fieldName === 'ids_parcelas') {
            return [];
        }

        return null;
    }

    private refreshReactiveDropdownMappings(): void {
        this.finalizeExternalDropdowns();
        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }

    private applyLoadedReactiveState(): void {
        this.form.model = {
            ...this.form.model,
            ...this.model,
        };
        this.syncModelFromForm();
        this.refreshReactiveDropdownMappings();
    }

    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return 'No se ha podido cargar el formulario de ' + this.reactiveFormErrorContext + ': ' + error.message;
        }

        return 'No se ha podido cargar el formulario de '
            + this.reactiveFormErrorContext
            + '. Revisa la conexión e inténtalo de nuevo.';
    }

    private getReactiveValueId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        return String(value ?? '');
    }

    private unsubscribeLocationCatalogs(): void {
        this.fincasApi.fincasGET.unsuscribe();

        if (this.usesLocationField('id_sector')) {
            this.fincasApi.sectoresGET.unsuscribe();
        }

        if (this.usesLocationField('ids_parcelas')) {
            this.fincasApi.parcelasGET.unsuscribe();
        }
    }

    private syncFilteredLocationCatalogs(): void {
        this.form.lists.fincas.filtered = this.form.lists.fincas.values;

        if (this.usesLocationField('id_sector')) {
            this.form.lists.sectores.filtered = this.form.lists.sectores.values;
        }

        if (this.usesLocationField('ids_parcelas')) {
            this.form.lists.parcelas.filtered = this.form.lists.parcelas.values;
        }
    }

    private usesLocationField(fieldName: MtoLocationCascadeField): boolean {
        return this.locationCascadeFields.includes(fieldName);
    }
}
